/* eslint new-cap: 0 */

import {
  createBrowserRouter,
  isRouteErrorResponse,
  Navigate,
  RouteObject,
  RouterProvider,
  useLocation,
} from "react-router";

import * as Sentry from '@sentry/react';

/* containers */
import LoginView from "./components/LoginView";
import ChangePasswordView from "./components/ChangePassword/ChangePasswordView";
import { LoadingAnimation } from "./components/LoadingAnimation";
import ReclamATC from "./components/ReclamATC/components/ReclamATC";
import CRMForm from "./components/CRMForm/components/CRMForm";
import UnsignedCRM from "./components/UnsignedCRM";
import ErrorRoutePage from "./components/Error/ErrorRoutePage";
import DinamicServerPage from "./components/ServerElements/components/DinamicServerPage";

import SignUpView from "./components/SignUp";
import ContractsTabs from "./components/Contracts/ContractsTabs";
import ContractDetailElectricity from "./components/Contracts/ContractDetailElectricity";
import ContractDetailGas from "./components/Contracts/ContractDetailGas";
import InvoicesTabs from "./components/Invoices/components/InvoicesTabs";
import InvoicesMultipoint from "./components/Invoices/components/InvoicesMultipointContent";
import InvoicesDownload from "./components/Invoices/InvoicesDownload";
import Contractation from "./components/Contractation/Contractation";
import ElectricityContractation from "./components/Contractation/ElectricityContractation";
import GasContractation from "./components/Contractation/GasContractation";
import UserResetPassword from "./components/UserResetPassword";
import ComparatorPower from "./components/ComparatorPower";
import ComparatorPrice from "./components/ComparatorPrice";
import GasPriceComparator from "./components/GasPriceComparator";

import Home from "./components/Home";

import RootPage from "./components/RootPage";
import useLangs from "./languages/hooks/useLangs";
import { AuthProtectionProvider } from "./components/Authentication/AuthProtectionProvider";
import { NavLanguageProvider } from "./components/NavLangProvider";
import { LayoutProvider } from "./components/LayoutProvider";
import NewsletterPage from "./components/Newsletter/NewsletterPage";
import FeatureFlag from "./components/FeatureFlag/FeatureFlag";
import Omip from "./components/Omip";
import { useSettingsContext } from "./components/Settings/hooks/useSettingsContext";
import ErrorComponentPage from "./components/Error/ErrorComponentPage";
import ErrorBoundaryComponent from "./components/Error/ErrorBoundaryComponent";
import DocumentRepository from "./components/DocumentRepository";
import ErrorComponentSearchParams from "./components/Error/ErrorComponentSearchParams";
import { AuthChecker } from "./components/Authentication/AuthChecker";
import { Settings } from "./components/Settings/types/settings";
import StablePrice from "./components/StablePrice/StablePrice";
import { useFeaturesEnabled } from "./components/ErpFeatures/hooks/useErpFeatures";
import { ErpFeatureKeys } from "./components/ErpFeatures/models/erpFeature";

type CustomRouteObject = {
  path: string;
  protection: "private" | "public" | "unprotected";
  activeCheck?: boolean;
  isError?: boolean;
} & RouteObject;

type RouteObjects = { [key: string]: CustomRouteObject };

export const generateTranslatedRouteElements = ({
  path, protection, element, isError = false, ...rest
}: CustomRouteObject) => {
  const { getAllLangsPath } = useLangs();

  return getAllLangsPath(path).map(translatedPath => ({
    path: translatedPath.replace(/{{/g, ":").replace(/}}/g, ""),
    element: (
      <NavLanguageProvider path={path}>
        <LayoutProvider path={path} isError={isError}>
          <AuthProtectionProvider path={path} protection={protection}>
            {element}
          </AuthProtectionProvider>
        </LayoutProvider>
      </NavLanguageProvider>
    ),
    ...rest
  } as RouteObject));
};

export const useRoutes = (Settings: Settings) => {

  const priceStableEnabled = useFeaturesEnabled([
    ErpFeatureKeys.PRICE_STABLE_API,
    ErpFeatureKeys.PRICE_STABLE_ERP
  ]);

  const privateRouteObjects = {
    invoices: {
      path: "common:url.invoices",
      element: <InvoicesTabs />,
      protection: "private"
    },
    invoicesDownload: {
      path: "common:url.invoices_download",
      element: <InvoicesDownload />,
      protection: "private"
    },
    invoicesMultipoint: {
      path: "common:url.invoices_multipoint",
      element: <InvoicesMultipoint />,
      protection: "private"
    },
    contractsElectricity: {
      path: "common:url.contracts.electricity.default",
      element: <ContractsTabs initContractType="electricity" />,
      protection: "private"
    },
    contractsGas: {
      path: "common:url.contracts.gas.default",
      element: <ContractsTabs initContractType="gas" />,
      protection: "private"
    },
    contactDetailElectricity: {
      path: "common:url.contracts.electricity.detail",
      element: <ContractDetailElectricity />,
      protection: "private"
    },
    contactDetailGas: {
      path: "common:url.contracts.gas.detail",
      element: <ContractDetailGas />,
      protection: "private"
    },
    contact: {
      path: "common:url.contact",
      element: <FeatureFlag flagName="crm.case"><CRMForm /></FeatureFlag>,
      protection: "private"
    },
    reclamations: {
      path: "common:url.reclamations",
      element: <ReclamATC />,
      protection: "private"
    },
    changePassword: {
      path: "common:url.changePassword",
      element: <ChangePasswordView />,
      protection: "private"
    },
    connect: {
      path: "common:url.connect",
      element: <LoadingAnimation />,
      protection: "private",
      activeCheck: Settings.features?.oidc
    },
    dynamic: {
      path: "common:url.dynamic",
      element: <DinamicServerPage />,
      protection: "private",
      activeCheck: Settings.features?.enableServerElements
    },
    omip: {
      path: "common:url.omip",
      element: <Omip />,
      protection: "private"
    },
    stablePrice: {
      path: "common:url.stable_price_data",
      element: <StablePrice />,
      protection: "private",
      activeCheck: priceStableEnabled && Settings.features?.stablePrice
    },
    documentRepository: {
      path: "common:url.document_repository",
      element: <DocumentRepository />,
      protection: "private",
      activeCheck: Settings.features?.showDocumentRepository
    },
    contractationElectricity: {
      path: "common:url.electricityContractation",
      element: <Contractation><ElectricityContractation /></Contractation>,
      protection: "private",
      activeCheck: Settings.contractation?.authenticate && Settings.contractation?.electricity?.enabled
    },
    contractationDual: {
      path: "common:url.dualContractation",
      element: <Contractation><ElectricityContractation dualContractation={true} /></Contractation>,
      protection: "private",
      activeCheck: Settings.contractation?.authenticate && Settings.contractation?.dual?.enabled
    },
    contractationGas: {
      path: "common:url.gasContractation",
      element: <Contractation><GasContractation /></Contractation>,
      protection: "private",
      activeCheck: Settings.contractation?.authenticate && Settings.contractation?.gas?.enabled
    }
  } as const satisfies RouteObjects;

  type AvailablePrivateRoutesObjects = keyof typeof privateRouteObjects;

  const publicRouteObjects = {
    login: {
      path: "common:url.login",
      element: <LoginView />,
      protection: "public",
      activeCheck: !Settings.features?.oidc
    },
    unsignedContact: {
      path: "common:url.unsigned_contact",
      element: <UnsignedCRM />,
      protection: "public"
    },
    signUp: {
      path: "common:url.activate",
      element: <SignUpView />,
      protection: "public",
      activeCheck: Settings.features?.signup
    },
    resetPassword: {
      path: "common:url.resetPassword",
      element: <UserResetPassword />,
      protection: "public"
    },
  } as const satisfies RouteObjects;

  type AvailablePublicRoutesObjects = keyof typeof publicRouteObjects;

  const unprotectedRouteObjects = {
    error: {
      path: "common:url.error",
      element: <ErrorComponentSearchParams />,
      isError: true,
      protection: "unprotected"
    },
    login: {
      path: "common:url.login",
      element: <LoginView />,
      protection: "unprotected",
      activeCheck: Settings.features?.oidc && Settings.oidc?.routes?.allowDefaultLogin
    },
    powerCalculator: {
      path: "common:url.powerCalculator",
      element: <ComparatorPower />,
      protection: "unprotected",
      activeCheck: Settings.powerCalculator?.enabled
    },
    priceComparator: {
      path: "common:url.priceComparator",
      element: <ComparatorPrice />,
      protection: "unprotected",
      activeCheck: Settings.comparator?.enabled
    },
    gasPriceComparator: {
      path: "common:url.gasPriceComparator",
      element: <GasPriceComparator />,
      protection: "unprotected",
      activeCheck: Settings.comparator?.enabled && Settings.features?.displayTypes.includes('gas')
    },
    loading: {
      path: "common:url.loading",
      element: <LoadingAnimation />,
      protection: "unprotected"
    },
    newsletter: {
      path: "common:url.newsletter",
      element: <NewsletterPage />,
      protection: "unprotected",
      activeCheck: Settings.newsletter?.enabled
    },
    contractationElectricity: {
      path: "common:url.electricityContractation",
      element: <Contractation><ElectricityContractation /></Contractation>,
      protection: "unprotected",
      activeCheck: !Settings.contractation?.authenticate && Settings.contractation?.electricity?.enabled
    },
    contractationDual: {
      path: "common:url.dualContractation",
      element: <Contractation><ElectricityContractation dualContractation={true} /></Contractation>,
      protection: "unprotected",
      activeCheck: !Settings.contractation?.authenticate && Settings.contractation?.dual?.enabled
    },
    contractationGas: {
      path: "common:url.gasContractation",
      element: <Contractation><GasContractation /></Contractation>,
      protection: "unprotected",
      activeCheck: !Settings.contractation?.authenticate && Settings.contractation?.gas?.enabled
    }
  } as const satisfies RouteObjects;

  type AvailableUnprotectedRoutesObjects = keyof typeof unprotectedRouteObjects;

  const defaultPrivateRoutes = [
    "invoices",
    "invoicesDownload",
    "invoicesMultipoint",
    "contractsElectricity",
    "contractsGas",
    "contactDetailElectricity",
    "contactDetailGas",
    "contact",
    "reclamations",
    "changePassword",
    "connect",
    "dynamic",
    "omip",
    "stablePrice",
    "documentRepository",
    "contractationElectricity",
    "contractationDual",
    "contractationGas"
  ] as const satisfies AvailablePrivateRoutesObjects[];

  const defaultPublicRoutes = [
    "login",
    "unsignedContact",
    "signUp",
    "resetPassword"
  ] as const satisfies AvailablePublicRoutesObjects[];

  const defaultUnprotectedRoutes = [
    "error",
    "login",
    "powerCalculator",
    "priceComparator",
    "gasPriceComparator",
    "loading",
    "newsletter",
    "contractationElectricity",
    "contractationDual",
    "contractationGas"
  ] as const satisfies AvailableUnprotectedRoutesObjects[];

  const createRoutes = (
    Settings: Settings,
    availablePrivateRouteObjects: AvailablePrivateRoutesObjects[] = defaultPrivateRoutes,
    availablePublicRouteObjects: AvailablePublicRoutesObjects[] = defaultPublicRoutes,
    availableUnprotectedRouteObjects: AvailableUnprotectedRoutesObjects[] = defaultUnprotectedRoutes
  ): RouteObject[] => {
    return [
      {
        path: "/",
        element: <AuthChecker><RootPage /></AuthChecker>,
        errorElement: <LayoutProvider isError={true}>
          <ErrorComponentPage />
        </LayoutProvider>,
        children: [

          {
            errorElement: <LayoutProvider isError={true}>
              <ErrorBoundaryComponent />
            </LayoutProvider>,
            children: [

              {
                index: true,
                element: (
                  <NavLanguageProvider>
                    <LayoutProvider>
                      <AuthProtectionProvider
                        protection={
                          Settings.home?.type == "tabGroups" ?
                            "unprotected" : "private"
                        }
                      >
                        <Home />
                      </AuthProtectionProvider>
                    </LayoutProvider>
                  </NavLanguageProvider>
                )
              },

              {
                path: "*",
                loader: () => { throw new Response("Not Found", { status: 404 }) }
              },

              ...(availablePrivateRouteObjects.map(
                routeObjectName => privateRouteObjects[routeObjectName]
              )
                .filter(privateRouteObject => (
                  // Type narrowing because privateRouteObject might or
                  // might not have activeCheck
                  ("activeCheck" in privateRouteObject) ? privateRouteObject.activeCheck : true
                ))
                .map(
                  privateRouteObject => generateTranslatedRouteElements(privateRouteObject)
                )
                .flat()
              ),

              ...(availablePublicRouteObjects.map(
                routeObjectName => publicRouteObjects[routeObjectName]
              )
                .filter(publicRouteObject => (
                  // Type narrowing because publicRouteObject might or
                  // might not have activeCheck
                  ("activeCheck" in publicRouteObject) ? publicRouteObject.activeCheck : true
                ))
                .map(
                  publicRouteObject => generateTranslatedRouteElements(publicRouteObject)
                )
                .flat()
              ),

              ...(availableUnprotectedRouteObjects.map(
                routeObjectName => unprotectedRouteObjects[routeObjectName]
              )
                .filter(unprotectedRouteObject => (
                  // Type narrowing because publicRouteObject might or
                  // might not have activeCheck
                  ("activeCheck" in unprotectedRouteObject) ? unprotectedRouteObject.activeCheck : true
                ))
                .map(
                  unprotectedRouteObject => generateTranslatedRouteElements(unprotectedRouteObject)
                )
                .flat()
              ),

            ]
          }

        ]
      }
    ];
  }

  return {
    createRoutes
  }

}

const RouterConfiguration = () => {
  const Settings = useSettingsContext();
  const { createRoutes } = useRoutes(Settings);

  const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(
    createBrowserRouter
  );

  const routes = createRoutes(Settings);

  const sentryWrappedRouter = sentryCreateBrowserRouter(routes);

  const maintenanceRouter = createBrowserRouter([
    {
      path: "*",
      element: <Navigate to={"/"} />,
    },
    {
      path: "/",
      element: <LayoutProvider isError={true}><ErrorComponentPage status={503} /></LayoutProvider>,
    }
  ]);

  return <RouterProvider
    router={Settings.maintenance?.activated ?
      maintenanceRouter : sentryWrappedRouter}
  />
};

export default RouterConfiguration;
